import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Product } from '../wysiwyg/packageUtils';
import type { ProductRecord } from '../types/index';
import { isActiveProduct, getProductOverride } from '../utils/productOverrides';
import useHistoricalProductOverride from './useHistoricalProductOverride';
import { getBrandlessProducts, getProductsList } from './useProducts';

type ProductCatalogState = {
  organizationId: number | null;
  products: Product[];
  isLoading: boolean;
  isIncomplete: boolean;
};

type UseProductCatalogParams = {
  organizationId: number | undefined;
  productOverrideId: number | string | null | undefined;
  allowArchivedProductOverrides: boolean;
};

const PRODUCT_PAGE_SIZE = 500;
const MAX_PRODUCT_PAGES = 200;

function normalizeProductOverrideId(productOverrideId: number | string | null | undefined) {
  const parsedOverrideId = productOverrideId == null ? null : Number(productOverrideId);
  return parsedOverrideId != null
    && Number.isSafeInteger(parsedOverrideId)
    && parsedOverrideId > 0
    ? parsedOverrideId
    : null;
}

async function fetchProductPages(
  organizationId: number,
  active: boolean | null,
  isCancelled: () => boolean,
): Promise<{ records: ProductRecord[]; complete: boolean }> {
  const records: ProductRecord[] = [];

  for (let page = 0; page < MAX_PRODUCT_PAGES; page += 1) {
    if (isCancelled()) return { records, complete: false };
    try {
      const results = await getProductsList({ organizationId, active, page, pageSize: PRODUCT_PAGE_SIZE });
      if (isCancelled()) return { records, complete: false };
      records.push(...results.filter(
        (product) => product?.name && Number.isFinite(product?.id) && (product?.id as number) > 0,
      ));
      if (results.length < PRODUCT_PAGE_SIZE) return { records, complete: true };
    } catch (error) {
      console.error('[useProductCatalog] Failed to fetch products page', page, error);
      return { records, complete: false };
    }
  }

  return { records, complete: false };
}

export default function useProductCatalog({
  organizationId,
  productOverrideId,
  allowArchivedProductOverrides,
}: UseProductCatalogParams) {
  const [catalogState, setCatalogState] = useState<ProductCatalogState>({
    organizationId: organizationId ?? null,
    products: [],
    isLoading: Boolean(organizationId),
    isIncomplete: false,
  });
  const requestIdRef = useRef(0);
  const [reloadVersion, setReloadVersion] = useState(0);

  const normalizedProductOverrideId = useMemo(
    () => normalizeProductOverrideId(productOverrideId),
    [productOverrideId],
  );

  useEffect(() => {
    if (!organizationId) {
      setCatalogState({
        organizationId: null,
        products: [],
        isLoading: false,
        isIncomplete: false,
      });
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;
    setCatalogState({
      organizationId,
      products: [],
      isLoading: true,
      isIncomplete: false,
    });

    const isCancelled = () => cancelled || requestId !== requestIdRef.current;

    const load = async () => {
      const { records, complete: productListComplete } = await fetchProductPages(
        organizationId,
        allowArchivedProductOverrides ? null : true,
        isCancelled,
      );
      if (isCancelled()) return;
      const seenIds = new Set(records.map((product) => String(product.id ?? '')));
      let brandlessListComplete = false;

      for (let page = 0; page < MAX_PRODUCT_PAGES; page += 1) {
        if (isCancelled()) return;
        try {
          const brandlessProducts = await getBrandlessProducts(organizationId, page, PRODUCT_PAGE_SIZE);
          if (isCancelled()) return;
          records.push(...brandlessProducts.filter((product) => {
            const id = String(product.id ?? '');
            if (!id || seenIds.has(id)) return false;
            if (!allowArchivedProductOverrides && !isActiveProduct(product)) return false;
            seenIds.add(id);
            return true;
          }));
          if (brandlessProducts.length < PRODUCT_PAGE_SIZE) {
            brandlessListComplete = true;
            break;
          }
        } catch (error) {
          console.error('[useProductCatalog] Failed to fetch brandless products page', page, error);
          break;
        }
      }
      if (isCancelled()) return;
      setCatalogState((current) => {
        const currentProducts = current.organizationId === organizationId ? current.products : [];
        const authoritativeActiveIds = new Set(
          currentProducts.filter(isActiveProduct).map((product) => Number(product.id)),
        );
        const recordIds = new Set(records.map((product) => Number(product.id)));
        return {
          organizationId,
          isLoading: false,
          isIncomplete: !productListComplete || !brandlessListComplete,
          products: [
            ...(records.filter((product) => !authoritativeActiveIds.has(Number(product.id))) as Product[]),
            ...currentProducts.filter((product) => (
              isActiveProduct(product) || !recordIds.has(Number(product.id))
            )),
          ],
        };
      });
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [organizationId, allowArchivedProductOverrides, reloadVersion]);

  const products = useMemo(
    () => (catalogState.organizationId === organizationId ? catalogState.products : []),
    [catalogState.organizationId, catalogState.products, organizationId],
  );

  const selectedListOverrideProduct = useMemo(() => getProductOverride(
    products,
    normalizedProductOverrideId,
    allowArchivedProductOverrides,
  ), [products, normalizedProductOverrideId, allowArchivedProductOverrides]);

  const selectedActiveOverrideProduct = useMemo(() => getProductOverride(
    products,
    normalizedProductOverrideId,
    false,
  ), [products, normalizedProductOverrideId]);

  const {
    product: lookedUpOverrideProduct,
    failed: historicalOverrideLookupFailed,
    isLoading: isLoadingHistoricalOverride,
    retry: retryHistoricalOverrideLookup,
  } = useHistoricalProductOverride(
    organizationId,
    normalizedProductOverrideId,
    normalizedProductOverrideId != null && !selectedActiveOverrideProduct,
  );

  useEffect(() => {
    if (!lookedUpOverrideProduct || !organizationId) return;
    if (!allowArchivedProductOverrides && !isActiveProduct(lookedUpOverrideProduct)) return;
    const overrideId = Number(lookedUpOverrideProduct.id);
    if (!Number.isFinite(overrideId) || overrideId <= 0) return;
    setCatalogState((current) => {
      const currentProducts = current.organizationId === organizationId ? current.products : [];
      return {
        ...current,
        organizationId,
        products: [
          ...currentProducts.filter((item) => Number(item.id) !== overrideId),
          { ...lookedUpOverrideProduct, id: overrideId },
        ],
      };
    });
  }, [lookedUpOverrideProduct, organizationId, allowArchivedProductOverrides]);

  const historicalOverrideProduct = lookedUpOverrideProduct && !isActiveProduct(lookedUpOverrideProduct)
    ? lookedUpOverrideProduct : null;
  const selectedOverrideProduct = selectedListOverrideProduct || (
    Number(historicalOverrideProduct?.id) === normalizedProductOverrideId ? historicalOverrideProduct : null
  );

  const retry = useCallback(() => {
    setReloadVersion((current) => current + 1);
    retryHistoricalOverrideLookup();
  }, [retryHistoricalOverrideLookup]);

  const isLoadingProducts = Boolean(
    organizationId
    && (catalogState.organizationId !== organizationId || catalogState.isLoading),
  );

  return {
    products,
    selectedOverrideProduct,
    selectedListOverrideProduct,
    selectedActiveOverrideProduct,
    isLoading: isLoadingProducts || isLoadingHistoricalOverride,
    isLoadingHistoricalOverride,
    isIncomplete: catalogState.organizationId === organizationId ? catalogState.isIncomplete : false,
    retry,
    historicalOverrideLookupFailed,
    retryHistoricalOverrideLookup,
  };
}
